import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Tooltip,
  Card,
  CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Business,
  Person,
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
  AttachMoney as MoneyIcon,
  CreditCard as CreditIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getCustomers, deleteCustomer } from '../../services/api';
import AddCustomer from './AddCustomer';

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await getCustomers();
      if (response.success) {
        setCustomers(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load customers');
      console.error('Customers error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await deleteCustomer(id);
        fetchCustomers();
      } catch (err) {
        setError(err.message || 'Failed to delete customer');
      }
    }
  };

  const handleView = (customer) => {
    setSelectedCustomer(customer);
    setOpenDialog(true);
  };

  const handleEdit = (id) => {
    navigate(`/customers/edit/${id}`);
  };

  const handleAddSuccess = (newCustomer) => {
    fetchCustomers();
    setError(null);
  };

  // Helper function to format date for CSV
  const formatDateForCSV = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Helper function to format currency for CSV (remove symbols)
  const formatCurrencyForCSV = (amount) => {
    if (!amount && amount !== 0) return '0.00';
    if (typeof amount === 'string') {
      // Remove currency symbols and commas
      return amount.replace(/[^0-9.-]/g, '');
    }
    return parseFloat(amount || 0).toFixed(2);
  };

  // Calculate customer statistics
  const calculateCustomerStats = () => {
    const stats = {
      total: customers.length,
      active: customers.filter(c => c.is_active).length,
      credit: customers.filter(c => parseFloat(c.credit_limit) > 0).length,
      corporate: customers.filter(c => c.customer_type === 'corporate').length,
      individual: customers.filter(c => c.customer_type === 'individual').length,
      totalOutstanding: customers.reduce((sum, c) => sum + (parseFloat(c.outstanding_balance) || 0), 0),
      totalCreditLimit: customers.reduce((sum, c) => sum + (parseFloat(c.credit_limit) || 0), 0)
    };
    return stats;
  };

  // Export to CSV function
  const handleExportCSV = () => {
    if (customers.length === 0) {
      alert('No customers to export');
      return;
    }

    // Define CSV headers
    const headers = [
      'Customer Code',
      'Customer Name',
      'Company Name',
      'Customer Type',
      'Email',
      'Phone',
      'Address',
      'City',
      'State',
      'Country',
      'Postal Code',
      'Tax ID',
      'Contact Person',
      'Payment Terms (days)',
      'Credit Limit (LKR)',
      'Available Credit (LKR)',
      'Outstanding Balance (LKR)',
      'Credit Utilization (%)',
      'Is Credit Customer',
      'Credit Status',
      'Last Payment Date',
      'Total Purchases',
      'Total Amount',
      'Is Active',
      'Notes',
      'Created Date',
      'Updated Date'
    ];

    // Prepare CSV data
    const csvData = customers.map(customer => {
      // Calculate credit utilization
      const creditLimit = parseFloat(customer.credit_limit) || 0;
      const outstanding = parseFloat(customer.outstanding_balance) || 0;
      const creditUtilization = creditLimit > 0 ? ((outstanding / creditLimit) * 100).toFixed(2) : 0;
      const availableCredit = creditLimit - outstanding;

      return [
        customer.customer_code || '',
        customer.name || '',
        customer.company_name || '',
        customer.customer_type || '',
        customer.email || '',
        customer.phone || '',
        customer.address || '',
        customer.city || '',
        customer.state || '',
        customer.country || '',
        customer.postal_code || '',
        customer.tax_id || '',
        customer.contact_person || '',
        customer.payment_terms || '',
        formatCurrencyForCSV(customer.credit_limit),
        formatCurrencyForCSV(availableCredit),
        formatCurrencyForCSV(customer.outstanding_balance),
        creditUtilization,
        customer.is_credit_customer ? 'Yes' : 'No',
        customer.credit_status || '',
        customer.last_payment_date ? formatDateForCSV(customer.last_payment_date) : '',
        customer.total_purchases || 0,
        customer.total_amount || 0,
        customer.is_active ? 'Active' : 'Inactive',
        customer.notes || '',
        formatDateForCSV(customer.created_at),
        formatDateForCSV(customer.updated_at)
      ];
    });

    // Add summary rows
    const stats = calculateCustomerStats();
    const summaryData = [
      [], // Empty row
      ['SUMMARY', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      ['Total Customers:', stats.total, '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      ['Active Customers:', stats.active, '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      ['Credit Customers:', stats.credit, '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      ['Corporate Customers:', stats.corporate, '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      ['Individual Customers:', stats.individual, '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      ['Total Outstanding Balance:', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', formatCurrencyForCSV(stats.totalOutstanding), '', '', '', '', '', '', '', '', '', ''],
      ['Total Credit Limit:', '', '', '', '', '', '', '', '', '', '', '', '', '', '', formatCurrencyForCSV(stats.totalCreditLimit), '', '', '', '', '', '', '', '', '', ''],
      ['Export Date:', new Date().toLocaleDateString(), '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '']
    ];

    // Combine all data
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(',')),
      ...summaryData.map(row => row.join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers_export_${new Date().toISOString().split('T')[0].replace(/-/g, '')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Add Customer Dialog */}
      <AddCustomer 
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onSuccess={handleAddSuccess}
      />

      {/* Customer Detail Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        {selectedCustomer && (
          <>
            <DialogTitle>
              Customer Details: {selectedCustomer.name}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Customer Code"
                    value={selectedCustomer.customer_code}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Customer Type"
                    value={selectedCustomer.customer_type}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={selectedCustomer.email}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={selectedCustomer.phone}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={selectedCustomer.address}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Credit Limit"
                    value={selectedCustomer.credit_limit_lkr}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Outstanding Balance"
                    value={selectedCustomer.outstanding_balance_lkr}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Payment Terms"
                    value={`${selectedCustomer.payment_terms} days`}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Status"
                    value={selectedCustomer.is_active ? 'Active' : 'Inactive'}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={selectedCustomer.company_name || 'Not specified'}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button 
                onClick={() => setOpenDialog(false)} 
                color="primary"
                variant="outlined"
              >
                Close
              </Button>
              <Button 
                onClick={() => {
                  setOpenDialog(false);
                  handleEdit(selectedCustomer.id);
                }}
                color="primary"
                variant="contained"
              >
                Edit Customer
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: '#2c3e50', fontWeight: 'bold' }}>
          Customers ({customers.length})
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ExportIcon />}
            onClick={handleExportCSV}
            disabled={customers.length === 0}
            size="small"
          >
            Export CSV
          </Button>
          
          <Tooltip title="Refresh">
            <IconButton 
              onClick={fetchCustomers}
              sx={{ color: '#3498db' }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddDialog(true)}
            sx={{
              backgroundColor: '#2ecc71',
              '&:hover': { backgroundColor: '#27ae60' }
            }}
          >
            Add Customer
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Customers
                  </Typography>
                  <Typography variant="h4">{calculateCustomerStats().total}</Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 40, color: '#3498db', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Active Customers
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {calculateCustomerStats().active}
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 40, color: '#2ecc71', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Credit Customers
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {calculateCustomerStats().credit}
                  </Typography>
                </Box>
                <CreditIcon sx={{ fontSize: 40, color: '#f39c12', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Outstanding
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {calculateCustomerStats().totalOutstanding.toLocaleString('en-LK', {
                      style: 'currency',
                      currency: 'LKR',
                      minimumFractionDigits: 0
                    })}
                  </Typography>
                </Box>
                <MoneyIcon sx={{ fontSize: 40, color: '#e74c3c', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Customers Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Code</strong></TableCell>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Contact</strong></TableCell>
              <TableCell><strong>Credit Limit</strong></TableCell>
              <TableCell><strong>Balance</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">
                    No customers found. Click "Add Customer" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              customers.map((customer) => (
                <TableRow 
                  key={customer.id}
                  hover
                  sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                >
                  <TableCell>{customer.customer_code}</TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">{customer.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {customer.company_name || 'No company name'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{customer.email}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {customer.phone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" color="primary">
                      {customer.credit_limit_lkr}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        color: customer.outstanding_balance === 0 ? 'success.main' : 'error.main',
                        fontWeight: 'bold'
                      }}
                    >
                      {customer.outstanding_balance_lkr}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      icon={customer.customer_type === 'corporate' ? <Business /> : <Person />}
                      label={customer.customer_type}
                      size="small"
                      sx={{ 
                        backgroundColor: customer.customer_type === 'corporate' ? '#e3f2fd' : '#f3e5f5',
                        color: customer.customer_type === 'corporate' ? '#1976d2' : '#7b1fa2'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={customer.is_active ? 'Active' : 'Inactive'} 
                      color={customer.is_active ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small" 
                        onClick={() => handleView(customer)}
                        sx={{ color: '#3498db' }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton 
                        size="small"
                        sx={{ color: '#f39c12' }}
                        onClick={() => handleEdit(customer.id)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small" 
                        onClick={() => handleDelete(customer.id)}
                        sx={{ color: '#e74c3c' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Customers;